import { useEffect, useRef, useState, useCallback } from "react";
import type { MetaFunction, LoaderFunctionArgs } from "@remix-run/node";
import { json, useLoaderData } from "@remix-run/react";
import { useTranslation } from "react-i18next";
import { useApi } from "~/api/ApiContext";
import { createApi } from "~/api/createApi.server";
import { Coachmark } from "~/components/Coachmark";
import { GuideBottomSheet } from "~/components/GuideBottomSheet";
import { LoadingScreen } from "~/components/LoadingScreen";
import { DefaultPlayer } from "~/components/Player/DefaultPlayer";
import { MockDefaultPlayer } from "~/components/Player/MockDefaultPlayer";
import { SnapScroll } from "~/components/SnapScroll";
import { useExposedCell } from "~/hooks/useExposedCell";
import { useSeasonContents } from "~/hooks/useSeasonContents";
import og from "~/images/og.png";
import type {
  GetContentsPayload,
  GetPlaysPayload,
  GetContentsEpisodesPayload,
  Deliberation as DeliberationType,
} from "~/payloads";
import {
  PlayerType,
  useContentDataStore,
  usePlayerStatusStore,
  usePlaybackStore,
  PlayerStatus,
} from "~/stores/playerStore";
import { playerCoachmarkCookie } from "~/utils/cookie";
import { formatCounts } from "~/utils/formatCounts";
import { getLocale } from "~/utils/getLocale.server";
import { gtag } from "~/utils/gtag";
import { handlePlaysResponse } from "~/utils/handlePlaysResponse";
import { ContentInfo } from "./components/ContentInfo";
import { Deliberation } from "./components/Deliberation";
import { Intro } from "./components/Intro";
import { PlayerCard } from "./components/PlayerCard";
import { ScrollButton } from "./components/ScrollButton";
import styles from "./route.module.css";
import i18next from "../../../i18n/i18next.server";

export async function loader({ params, request }: LoaderFunctionArgs) {
  const serversideApi = await createApi(request);

  const seasonData = await serversideApi.getJson<GetContentsPayload>({
    endpoint: `/_/aa00/contents/${params.contentId}`,
  });

  const locale = await getLocale(request);
  const t = await i18next.getFixedT(locale, ["contentPage", "common"]);

  const meta = [
    { title: seasonData.result.title },
    {
      name: "keywords",
      content: t("contentPage:og.keywords", { title: seasonData.result.title }),
    },
    { name: "description", content: seasonData.result.story },
    { property: "og:title", content: seasonData.result.title },
    { property: "og:image", content: seasonData.result.poster ?? og },
  ];

  return json({
    seasonData,
    contentId: params.contentId,
    meta,
  });
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data || !("meta" in data)) {
    return [];
  }

  return data.meta;
};

export default function Content() {
  const { api } = useApi();
  const { i18n, t } = useTranslation(["common", "contentPage"]);
  const { seasonData, contentId } = useLoaderData<typeof loader>();
  const { activeId, disconnectIo, observeCell, CELL_CLASSNAME } =
    useExposedCell();
  const currentDataRef = useRef(useContentDataStore.getState().data);
  const controlsVisibleTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const deliberationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const { data: seasonEpisodes } = useSeasonContents(contentId ?? "");

  const episodes = seasonEpisodes
    ? seasonEpisodes.reduce(
        (prev: GetContentsEpisodesPayload["result"], cur) => [
          ...prev,
          ...cur.result,
        ],
        [],
      )
    : [];

  const freeEpisodes = episodes.filter(episode => episode.freeViewable);

  const [controlsVisible, setControlsVisible] = useState(true);
  const [deliberation, setDeliberation] = useState<DeliberationType>();
  const [deliberationVisible, setDeliberationVisible] = useState(false);
  const [deliberationShown, setDeliberationShown] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [replay, setReplay] = useState(false);
  const [likes, setLikes] = useState<{ [key: string]: boolean }>({});
  const [showCoachmark, setShowCoachmark] = useState(false);
  const [lastScrollClicked, setLastScrollClicked] = useState<boolean>(false);

  const toggleControlsVisible = useCallback(() => {
    if (controlsVisibleTimeoutRef.current) {
      clearTimeout(controlsVisibleTimeoutRef.current);
    }
    setControlsVisible(prev => !prev);
  }, []);

  useEffect(() => {
    gtag.sendEvent({
      eventName: "screen_view",
      wEventId: 8,
      screenName: "player",
      wSeasonCode: seasonData.result.id,
    });
    observeCell();

    if (!playerCoachmarkCookie.get()) {
      setShowCoachmark(true);
      playerCoachmarkCookie.set(true);

      setTimeout(() => {
        setShowCoachmark(false);
      }, 3300);
    }

    return () => {
      disconnectIo();
    };
  }, []);

  useEffect(() => {
    if (controlsVisible && !usePlaybackStore.getState().paused) {
      controlsVisibleTimeoutRef.current = setTimeout(() => {
        setControlsVisible(false);
      }, 2500);
    }

    if (!deliberationShown) {
      setDeliberationVisible(!controlsVisible);

      if (!controlsVisible) {
        deliberationTimeoutRef.current = setTimeout(() => {
          setDeliberationShown(true);
        }, 3000);
      }
    }
  }, [controlsVisible]);
  // TODO: visible 로직은 한곳으로 모으자

  function restartControlsVisibleTimeout() {
    if (controlsVisibleTimeoutRef.current) {
      clearTimeout(controlsVisibleTimeoutRef.current);
    }

    controlsVisibleTimeoutRef.current = setTimeout(() => {
      setControlsVisible(false);
    }, 3000);
  }

  useEffect(() => {
    if (deliberationShown && deliberationTimeoutRef.current) {
      clearTimeout(deliberationTimeoutRef.current);
      setDeliberationVisible(false);
    }
  }, [deliberationShown]);

  const currentEpisodeNumber = freeEpisodes?.find(
    episode => episode.id === activeId,
  )?.displayNumber;

  useEffect(
    () =>
      useContentDataStore.subscribe(
        state => state.data,
        data => {
          currentDataRef.current = data;
        },
      ),
    [],
  );

  useEffect(() => {
    if (
      currentDataRef.current &&
      "id" in currentDataRef.current &&
      currentDataRef.current?.id === activeId
    ) {
      return;
    }

    let cancelled = false;

    function resetStore() {
      return new Promise<void>(resolve => {
        if (!cancelled) {
          usePlayerStatusStore.setState({
            playerStatus: PlayerStatus.LOADING,
          });
          useContentDataStore.setState({
            data: undefined,
            meta: undefined,
            error: undefined,
          });
        }
        resolve();
      });
    }

    if (typeof activeId !== "undefined") {
      resetStore().then(() => {
        api
          .getJson<GetPlaysPayload>({
            endpoint: `/_/aa00/plays/${activeId}`,
          })
          .then(response => {
            const result = handlePlaysResponse({
              meta: PlayerType.FULL,
              result: { ...response.result, id: activeId },
            });
            useContentDataStore.setState({
              meta: result.meta,
              data: result.result,
              error: undefined,
            });
            setDeliberation(response.result.deliberation);
          })
          .then(() => {
            usePlayerStatusStore.setState({
              playerStatus: PlayerStatus.READY,
            });
          });
      });
    }

    // eslint-disable-next-line consistent-return
    return () => {
      cancelled = true;
    };
  }, [activeId]);

  const handleEnded = useCallback(() => {
    const snapScroll = document.getElementById("snapScroll");
    const scrollHeight = snapScroll?.offsetHeight || 0;
    const currentEpisodeIndex = freeEpisodes.findIndex(
      episode => episode.id === activeId,
    );

    if (currentEpisodeIndex === freeEpisodes.length - 1) {
      setShowGuide(true);
    } else {
      snapScroll?.scrollTo(0, (currentEpisodeIndex + 1) * scrollHeight);
    }
  }, [activeId]);

  function handleGuideClose() {
    setShowGuide(false);
    setReplay(true);
  }

  function handleScroll(direction: "up" | "down") {
    const snapScroll = document.getElementById("snapScroll");
    const scrollHeight = snapScroll?.offsetHeight || 0;
    if (!snapScroll) {
      return;
    }

    if (
      direction === "down" &&
      freeEpisodes.findIndex(episode => episode.id === activeId) ===
        freeEpisodes.length - 1
    ) {
      setLastScrollClicked(true);
      setShowGuide(true);
      return;
    }

    if (direction === "up") {
      snapScroll.scrollTo({
        top: snapScroll.scrollTop - scrollHeight,
        behavior: "smooth",
      });
    } else {
      snapScroll.scrollTo({
        top: snapScroll.scrollTop + scrollHeight,
        behavior: "smooth",
      });
    }
  }

  return (
    <div className={styles.container}>
      {!!freeEpisodes && freeEpisodes.length ? (
        <>
          <ContentInfo
            description={`${currentEpisodeNumber}${t("common:episode")} / ${seasonData.result.episodesCount}${t("common:episode")}`}
            episodeNumber={Number(currentEpisodeNumber)}
            seasonId={seasonData.result.id}
            title={seasonData.result.title ?? ""}
            visible={controlsVisible}
          />
          {deliberation && (
            <Deliberation
              deliberation={deliberation}
              visible={deliberationVisible}
            />
          )}
          <Intro />
          <SnapScroll withMargin={false}>
            {freeEpisodes.map((episode, index) => (
              <PlayerCard
                key={episode.id}
                className={CELL_CLASSNAME}
                id={episode.id}
              >
                {(
                  typeof activeId === "undefined"
                    ? episode.id === contentId
                    : episode.id === activeId
                ) ? (
                  <DefaultPlayer
                    key={episode.id}
                    contentCode={episode.id}
                    controlsVisible={controlsVisible}
                    controlsVisibleTimeoutRef={controlsVisibleTimeoutRef}
                    id={episode.id}
                    isLastEpisode={index === freeEpisodes.length - 1}
                    lastScrollClicked={lastScrollClicked}
                    liked={likes[episode.id]}
                    likesCount={formatCounts(
                      likes[episode.id]
                        ? episode.likesCount + 1
                        : episode.likesCount,
                      i18n.language as "ko" | "ja" | "en",
                    )}
                    replay={replay}
                    restartControlsVisibleTimeout={
                      restartControlsVisibleTimeout
                    }
                    stillcut=""
                    onDefaultScreenClick={toggleControlsVisible}
                    onEnded={handleEnded}
                    onLike={() =>
                      setLikes(prev => ({
                        ...prev,
                        [episode.id]: !prev[episode.id],
                      }))
                    }
                    onReplay={() => setReplay(false)}
                    onSetLastScrollClicked={setLastScrollClicked}
                    onShowGuide={() => setShowGuide(true)}
                  />
                ) : (
                  <MockDefaultPlayer
                    key={episode.id}
                    id={episode.id}
                    observeCell={observeCell}
                    // stillcut={episode}
                  >
                    <LoadingScreen />
                  </MockDefaultPlayer>
                )}
              </PlayerCard>
            ))}
          </SnapScroll>
          <div className={styles.scrollButtons}>
            <ScrollButton
              direction="up"
              status={
                freeEpisodes.findIndex(episode => episode.id === activeId) === 0
                  ? "disabled"
                  : "default"
              }
              onClick={() => {
                handleScroll("up");
              }}
            />
            <ScrollButton
              direction="down"
              status={showGuide ? "disabled" : "default"}
              onClick={() => {
                handleScroll("down");
              }}
            />
          </div>
          {showGuide ? <GuideBottomSheet onClose={handleGuideClose} /> : null}
        </>
      ) : (
        <LoadingScreen />
      )}
      {showCoachmark ? <Coachmark /> : null}
    </div>
  );
}
